// arbt — turns the shared filter panel (snippets/arbt-collection-filters.liquid) into a
// bottom-sheet modal below 1000px, the same breakpoint arbt-collection-filters.css already
// uses for the sidebar/stacked switch. Desktop is untouched: the dialog is a `display:
// contents` no-op there, so `.arbt-facets` lands in the sidebar grid column exactly as
// before this file existed.
//
// WHY NOT AJAX-instant filtering inside the modal: every facet change on this store submits
// via `<form is="facet-form" update-on-change>`, which replaces the WHOLE
// `#shopify-section-…` subtree on every change (assets/theme.js facet:update handler). A
// modal open state living on an element inside that subtree cannot survive a change made
// while it is open — the fresh HTML has no `open` attribute, so the sheet would appear to
// slam shut on the first tap. Rather than fight that with a MutationObserver that reopens
// state after every swap, the modal removes `update-on-change` for the life of the sheet
// (mobile widths only) so taps accumulate quietly, and the SINGLE submit happens exactly
// when the sheet closes — matching the moment the user actually expects the grid to update,
// and matching the mobile SORT drawer's own established close-submits contract
// (snippets/facets-vertical.liquid) instead of inventing a second one.
//
// Each `<dialog class="arbt-facets-dialog">` is paired with an `<arbt-filters-dialog-init>`
// element placed just before its closing tag — that pairing, and firing wiring from
// connectedCallback rather than a MutationObserver, is the same idiom
// arbt-facet-announcer already uses in the same file: connectedCallback fires exactly when
// the node lands, on first paint AND on every facet:update AJAX replacement alike, so a
// fresh dialog re-wires itself with no polling and no listener to leak.

const MOBILE_QUERY = window.matchMedia('(max-width: 999.98px)');
const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
const VISIBLE_CLASS = 'arbt-facets-dialog--visible';
// Whites out the page canvas behind Safari's floating address bar, which the top layer
// is clipped out of, and locks the page scroll a native modal leaves running. The CSS
// side carries the full reasoning.
const SHEET_OPEN_CLASS = 'arbt-facets-sheet-open';

function wireDialog(dialog) {
  const trigger = document.querySelector(`[aria-controls="${dialog.id}"]`);
  const doneButton = dialog.querySelector('.arbt-facets-dialog__footer button');
  const closeButton = dialog.querySelector('.arbt-facets-dialog__close');
  const form = dialog.querySelector('form[is="facet-form"]');

  function setMobileMode(isMobile) {
    if (isMobile) {
      form?.removeAttribute('update-on-change');
      // Collapse the SSR-open panel into the closed sheet the trigger button reveals.
      // Only relevant before the shopper has interacted with it: a sheet they actually
      // opened is left alone here — the resize branch below is what unwinds that case.
      if (dialog.hasAttribute('open') && !dialog.hasAttribute('data-arbt-modal-open')) {
        dialog.removeAttribute('open');
      }
    } else {
      // Crossing back to desktop while the sheet happens to be open (device rotation,
      // resizing dev tools mid-interaction): close it properly first, so a pending
      // change still submits, before restoring the always-open sidebar presentation.
      if (dialog.hasAttribute('data-arbt-modal-open')) {
        dialog.close();
      }
      form?.setAttribute('update-on-change', '');
      if (!dialog.hasAttribute('open')) dialog.setAttribute('open', '');
    }
  }

  function openModal(event) {
    event.preventDefault();
    dialog.removeAttribute('role');
    dialog.setAttribute('data-arbt-modal-open', '');
    dialog.showModal();
    document.documentElement.classList.add(SHEET_OPEN_CLASS);
    trigger?.setAttribute('aria-expanded', 'true');
    // The sheet has to paint off-screen (its CSS rest state) at least once before
    // adding the class that transitions it in — a single rAF can land in the same
    // frame showModal()'s style recalc does, so wait a second one.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => dialog.classList.add(VISIBLE_CLASS));
    });
  }

  // Standard "click landed outside the dialog's own box" recipe: showModal() paints the
  // ::backdrop across the rest of the viewport, and a click there still targets `dialog`
  // itself (::backdrop isn't a separate hit-testable node), so a bounding-rect check is
  // the reliable way to tell a backdrop tap from a tap inside the sheet.
  //
  // `event.detail` guards a real bug this caught in testing, not just a synthetic-click
  // artifact: a `click` fired by activating a checkbox from the KEYBOARD (Space/Enter)
  // still bubbles up to this listener, but carries `clientX`/`clientY` of 0,0 — which
  // reads as "outside the sheet" and closed the modal on every keyboard checkbox toggle.
  // A real pointer click always has `detail >= 1`; only a mouse/touch click can actually
  // be a backdrop dismissal in the first place, so anything else is left alone here.
  function closeOnBackdropClick(event) {
    if (!dialog.open || event.detail === 0) return;
    const rect = dialog.getBoundingClientRect();
    const insideSheet =
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom &&
      event.clientX >= rect.left &&
      event.clientX <= rect.right;
    if (!insideSheet) requestClose();
  }

  // Escape fires `cancel` before the browser closes the dialog on its own; taking that
  // over is what lets Escape play the same exit transition as every other way out.
  function onCancel(event) {
    event.preventDefault();
    requestClose();
  }

  // The one way any caller asks the sheet to close. Plays the exit transition first
  // (skipped outright under reduced motion, or if it was never shown — e.g. a stray
  // `cancel` while still mid-open-animation) and only calls the real `.close()` once
  // the sheet has actually left the screen, so the dialog doesn't just vanish under it.
  function requestClose() {
    if (REDUCED_MOTION_QUERY.matches || !dialog.classList.contains(VISIBLE_CLASS)) {
      dialog.close();
      return;
    }
    dialog.classList.remove(VISIBLE_CLASS);
    let closed = false;
    const finishClose = () => {
      if (closed) return;
      closed = true;
      dialog.close();
    };
    dialog.addEventListener(
      'transitionend',
      (event) => {
        if (event.target === dialog && event.propertyName === 'transform') finishClose();
      },
      { once: true },
    );
    // Belt-and-suspenders: a backgrounded tab (or any other dropped transitionend) must
    // never stall the sheet indefinitely half-closed — the transition is 220ms, so
    // anything still open a few frames past that closes anyway.
    setTimeout(finishClose, 400);
  }

  // One code path for every way to leave the sheet — Escape, a backdrop tap, or the
  // Apply button all route through requestClose() above, which only ever reaches the
  // real `.close()` (and therefore this) once the exit transition has actually run.
  function onClose() {
    dialog.classList.remove(VISIBLE_CLASS);
    dialog.removeAttribute('data-arbt-modal-open');
    document.documentElement.classList.remove(SHEET_OPEN_CLASS);
    dialog.setAttribute('role', 'none');
    trigger?.setAttribute('aria-expanded', 'false');
    if (form) {
      if (form.requestSubmit) {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { cancelable: true }));
      }
    }
    trigger?.focus();
  }

  trigger?.addEventListener('click', openModal);
  doneButton?.addEventListener('click', requestClose);
  closeButton?.addEventListener('click', requestClose);
  dialog.addEventListener('click', closeOnBackdropClick);
  dialog.addEventListener('cancel', onCancel);
  dialog.addEventListener('close', onClose);
  // This dialog is discarded (not just hidden) by the next facet:update swap — the whole
  // section is replaced — so the listener above goes with it; a fresh dialog re-wires its
  // own listener via arbt-filters-dialog-init the same way. Nothing to unsubscribe here.
  MOBILE_QUERY.addEventListener('change', (event) => setMobileMode(event.matches));

  setMobileMode(MOBILE_QUERY.matches);
}

class ArbtFiltersDialogInit extends HTMLElement {
  connectedCallback() {
    const dialog = this.closest('dialog.arbt-facets-dialog');
    if (dialog) wireDialog(dialog);
  }
}

if (!window.customElements.get('arbt-filters-dialog-init')) {
  window.customElements.define('arbt-filters-dialog-init', ArbtFiltersDialogInit);
}
